import { assert } from '../../lib/assert-sidecar'
import { distanceUtil } from '../../lib/distanceUtil-sidecar'
import { l } from '../../lib/imports'
import { rando } from '../../lib/rando-sidecar'
import { common } from '../../main/common-sidecar'
import { objs } from '../echo/menuController'
import { EVoyageCrewDamageType } from './multi/voyageMessageCrewHurt'
import { voyageMessageCrewHurt } from './multi/voyageMessageCrewHurt-sidecar'
import type { IVoyageBody } from './voyageBodies'
import { voyageBodies } from './voyageBodies-sidecar'
import { EVoyageCarryableType } from './voyageCarryableData'
import { voyageColors } from './voyageColors-sidecar'
import { EVoyageCrewType, IVoyageCrew } from './voyageCrew'
import { voyageCrew } from './voyageCrew-sidecar'
import { voyageGameStatus } from './voyageGameStatus-sidecar'
import { voyageParticles } from './voyageParticles-sidecar'
import type { IVoyageProgressBar } from './voyageProgressBars'
import { voyageProgressBars } from './voyageProgressBars-sidecar'
import { voyageSounds } from './voyageSounds-sidecar'
import type { IVoyageSplatter } from './voyageSplatters'
import { voyageSplatters } from './voyageSplatters-sidecar'
import { voyageSystems } from './voyageSystems-sidecar'
import { voyageTilemap } from './voyageTilemap-sidecar'
import { voyageWebsockets } from './voyageWebsockets-sidecar'

const _offsets9 = [
  [0, 0],

  [0, -1],
  [1, -1],
  [1, 0],
  [1, 1],

  [0, 1],
  [-1, 1],
  [-1, 0],
  [-1, -1],
]

export interface IVoyageCrewCrew {
  fireExtinguishTimer: number
  repairTimer: number
  useSystemTimer: number
  oxygenTimer: number

  mopTimer: number
  medpackTimer: number

  coffeeSteps: number

  statKills: number
  statDamageGiven: number
  statHeals: number
  statRepairs: number
  statFiresFought: number

  body: IVoyageBody | null
  progressBar: IVoyageProgressBar | null
}

export function create() {
  let b = voyageBodies.create()

  let d: IVoyageCrewCrew = {
    fireExtinguishTimer: 0,
    useSystemTimer: 0,
    repairTimer: 0,
    oxygenTimer: 0,
    mopTimer: 0,
    medpackTimer: 0,

    statKills: 0,
    statDamageGiven: 0,
    statHeals: 0,
    statRepairs: 0,
    statFiresFought: 0,
    coffeeSteps: 0,

    body: b,

    progressBar: null,
  }
  return d
}

export function update(obj: IVoyageCrew) {
  let ds = common.getUpdateDeltaScaled()
  let d = obj.crewState
  assert.notNil(d)

  setProgress(obj, 0)

  if (obj.isDead) {
    return
  }
  if (obj.isDying) {
    return
  }

  let status = voyageGameStatus.get()
  status.crewCount++

  const sleepEffectTime = 0.5
  obj.sleepyEffectTimer += ds
  if (obj.sleepyEffectTimer > sleepEffectTime) {
    obj.sleepyEffectTimer -= sleepEffectTime

    if (obj.isPassedOut) {
      let p = voyageParticles.create('zzz', obj.x, obj.y)
      p.image.tint = voyageColors.red
    } else {
      if (obj.energy <= 60) {
        let p = voyageParticles.create('zzz', obj.x, obj.y)
        p.image.tint = voyageColors.yellow
      }
    }
  }

  obj.energyTimer += ds
  const energyTime = 10
  const energyRate = 1

  if (!obj.isPassedOut) {
    if (obj.energyTimer > energyTime) {
      // Some randomness in energy
      obj.energyTimer -= rando.fj(energyTime, 0.25)
      voyageCrew.hurtEnergy(obj, energyTime * energyRate)
    }
  } else {
    const passedOutTime = 10
    const passedOutEnergyRate = -5
    obj.passedOutTimer += ds
    if (obj.passedOutTimer > passedOutTime) {
      obj.passedOutTimer = 0
      voyageCrew.hurtEnergy(obj, passedOutTime * passedOutEnergyRate)
    }
  }

  const oxygenTime = 2

  d.oxygenTimer += ds
  if (d.oxygenTimer > oxygenTime) {
    d.oxygenTimer -= oxygenTime

    if (status.oxygen < 15) {
      if (rando.chance(0.25)) {
        let phrases = [
          'It is tough to breathe',
          'Someone check life support',
          '',
        ]
        voyageCrew.say(obj, rando.sample(phrases))
      }
    }

    if (status.oxygen < 5) {
      let phrases = [
        '*gasp*',
        'Oxygen!',
        'O2!',
        'I need air!',
        'Get to life support quick!',
        '',
        '',
      ]
      voyageCrew.say(obj, rando.sample(phrases))
      voyageCrew.hurt(obj, 5, EVoyageCrewDamageType.NoOxygen, 0) //'Asphyxiation')
      voyageParticles.createMultiple('oxygen', obj.x, obj.y, rando.i(1, 3))
    }
  }

  // TODO: does this make it so most effects don't work while moving?
  if (obj.path) {
    return // moving
  }

  if (obj.items.length > 0) {
    // Mopping (per client)
    if (voyageCrew.isHolding(obj, EVoyageCarryableType.Mop)) {
      const cleaningTime = 0.25
      d.mopTimer += ds

      if (d.mopTimer > cleaningTime) {
        d.mopTimer -= cleaningTime

        let closestSplatter: IVoyageSplatter | null = null as any
        let closestSplatterDist = 48
        l.forEach(voyageSplatters.getAll(), (c) => {
          if (!c.isDead) {
            let dist = distanceUtil.distanceNormalXY(c.image, obj)
            if (dist < closestSplatterDist) {
              closestSplatterDist = dist
              closestSplatter = c
            }
          }
        })

        if (closestSplatter) {
          voyageSplatters.kill(closestSplatter)
        }
      }
    }

    // Cannot heal at low oxygen to avoid exploit
    if (
      voyageCrew.isHolding(obj, EVoyageCarryableType.Medpack) &&
      status.oxygen >= 10
    ) {
      const medpackTime = 0.25
      d.medpackTimer += ds

      if (d.medpackTimer > medpackTime) {
        d.medpackTimer -= medpackTime

        let closestCrew: IVoyageCrew | null = null as any
        let closestCrewDist = 48
        l.forEach(voyageCrew.getAll(), (c) => {
          if (
            !c.isDead &&
            c !== obj &&
            c.hitpoints < 110 &&
            c.type === EVoyageCrewType.Crew
          ) {
            let dist = distanceUtil.distanceNormalXY(c.image, obj)
            if (dist < closestCrewDist) {
              closestCrewDist = dist
              closestCrew = c
            }
          }
        })

        if (closestCrew) {
          let toHeal = closestCrew
          voyageParticles.createMultiple('heal', toHeal.x, toHeal.y, 1)
          if (voyageWebsockets.getIsHost()) {
            let healRate = 10
            let newHitpoints = toHeal.hitpoints + medpackTime * healRate
            if (newHitpoints > 125) {
              newHitpoints = 125
            }
            voyageMessageCrewHurt.sendMessageAndImmediatelyExecute({
              crewId: toHeal.id,
              newHitpoints: newHitpoints,
              type: EVoyageCrewDamageType.Heal,
              byCrewId: obj.id,
            })
          }
        }
      }
    }
  }

  let tCur = voyageTilemap.getSafeIJ(obj)
  if (tCur) {
    voyageSystems.useSystem(obj, tCur)
  }

  if (voyageGameStatus.get().hasFire.isActive) {
    d.fireExtinguishTimer += ds
    let fireExtinguishTime = 1.5
    if (voyageCrew.isHolding(obj, EVoyageCarryableType.Extinguisher)) {
      fireExtinguishTime /= 3
    }

    if (d.fireExtinguishTimer > fireExtinguishTime) {
      d.fireExtinguishTimer -= rando.fj(fireExtinguishTime, 0.25)

      let tHere = voyageTilemap.getSafeIJ(obj)
      if (tHere) {
        if (tHere.fire) {
          const fireDamage = 25 * fireExtinguishTime
          voyageCrew.hurt(obj, fireDamage, EVoyageCrewDamageType.Fire, 0)
        }
      }

      for (let i = 0; i < _offsets9.length; i++) {
        let os = _offsets9[i]
        let tCheck = voyageTilemap.getSafeOffset(obj, os[0], os[1])
        if (tCheck) {
          if (tCheck.fire) {
            voyageCrew.hurt(
              tCheck.fire,
              8,
              EVoyageCrewDamageType.FireExtinguisher,
              obj.id,
            )
            voyageSounds.playFireExtinguisher()
            voyageParticles.createMultiple(
              'fire-extinguisher',
              tCheck.x,
              tCheck.y + 8,
              5,
            )
            // Only one
            break
          }
        }
      }
    }
  }

  if (voyageGameStatus.get().hasBreach.isActive) {
    d.repairTimer += ds
    let repairTime = 0.75
    if (voyageCrew.isHolding(obj, EVoyageCarryableType.Wrench)) {
      repairTime /= 3
    }

    if (d.repairTimer > repairTime) {
      d.repairTimer -= rando.fj(repairTime, 0.25)

      for (let i = 0; i < _offsets9.length; i++) {
        let os = _offsets9[i]
        let tCheck = voyageTilemap.getSafeOffset(obj, os[0], os[1])
        if (tCheck) {
          if (tCheck.breach) {
            voyageCrew.hurt(
              tCheck.breach,
              7,
              EVoyageCrewDamageType.Repair,
              obj.id,
            )
            voyageSounds.playRepair()

            voyageParticles.createMultiple(
              'heal-hull',
              tCheck.x,
              tCheck.y + 8,
              rando.i(2, 4),
            )

            // Only one
            break
          }
        }
      }
    }
  }
}

export function setProgress(obj: IVoyageCrew, v: number) {
  // assert.notNil(obj.crewState)
  if (obj.crewState) {
    if (!obj.crewState.progressBar) {
      obj.crewState.progressBar = voyageProgressBars.create()
    }
    voyageProgressBars.setValue(obj.crewState.progressBar, v)
  }
}
